import {
	BoundingSphere,
	Cartesian3,
	Cesium3DTileset,
	Math as CesiumMath,
	defined,
	type Entity,
	HeadingPitchRange,
	type JulianDate,
	Matrix3,
	Matrix4,
	PrimitiveCollection,
	Quaternion,
	Ray,
	SceneMode,
	Transforms,
	type Viewer,
} from 'cesium';

/**
 * Camera placement for "zoom to damage".
 *
 * A damage sits on a surface of the model (a wall, the ground, the underside of a deck).
 * The camera is put in front of that surface, along its outward normal, so the damage is
 * actually visible after the flight. Which side of the surface is "in front" is asked of the
 * scene's geometry (see `hasLineOfSight`); only when it cannot answer do the hints in
 * `getDamageViewOffset` decide. Vectors in the local functions are east/north/up (x/y/z)
 * metres unless stated otherwise.
 */

/** Distance the camera keeps from a damage (metres). */
export const POLYGON_RANGE = 15;
export const POINT_RANGE = 25;

/** Pitch used when the surface orientation is unknown: points, and degenerate polygons. */
const POLYGON_FALLBACK_PITCH = CesiumMath.toRadians(-45);
const POINT_PITCH = CesiumMath.toRadians(-30);

/** The camera looks at a surface from a little above its normal, so a wall keeps some ground context. */
const TILT_BELOW_NORMAL = CesiumMath.toRadians(20);

/** Keeps the view off the vertical, so the heading still means something on floors and ceilings. */
const MAX_PITCH = CesiumMath.toRadians(75);

/** A direction with less horizontal component than this (unit vector) has no usable heading. */
const MIN_HORIZONTAL = 1e-6;

/** A surface tilted less than this from horizontal is a floor or ceiling: its tilt is noise, not a facing. */
const NEAR_VERTICAL = CesiumMath.toRadians(10);

/**
 * |cos| between a normal and a side hint below which the hint does not decide the side. A
 * camera almost straight above a wall says nothing about which side of it the camera is on.
 */
const MIN_DECISIVE_COSINE = 0.2;

/** A sight line that lands farther than this from the damage (metres) did not reach it. */
const SIGHT_TOLERANCE = 2;

/** Longest wait for the tiles along a sight line to load before the hints decide instead (ms). */
export const SIGHT_TIME_LIMIT_MS = 4000;

/** Tilesets wider than this (world-wide imagery) say nothing about where "outside" a structure is. */
const MAX_MODEL_RADIUS = 50_000;

type PrimitiveLike = { length: number; get(index: number): unknown };

type RayHit = { position?: Cartesian3 } | undefined;

/**
 * The part of `Scene` used for sight lines. Neither pick is in Cesium's public typings (both
 * are marked private, present since 1.50), so they are looked up on the scene at runtime.
 */
type RayPickingScene = {
	mode: SceneMode;
	pickFromRay?: (ray: Ray, objectsToExclude?: object[], width?: number) => RayHit;
	pickFromRayMostDetailed?: (ray: Ray, objectsToExclude?: object[], width?: number) => Promise<RayHit>;
};

type ScenePrimitive = {
	show?: boolean;
	isLayer?: boolean;
	boundingSphere?: BoundingSphere;
	length?: number;
	get?: (index: number) => unknown;
};

/**
 * Unit normal of a polygon from its vertices, in the vertices' coordinate system.
 * The sign follows the winding order. Undefined when the vertices do not span a plane.
 */
export function polygonNormal(positions: Cartesian3[]): Cartesian3 | undefined {
	if (positions.length < 3) return undefined;

	// Sum of the triangle-fan cross products, taken relative to the first vertex so
	// Earth-scale (ECEF) coordinates do not lose precision.
	const origin = positions[0];
	const sum = new Cartesian3(0, 0, 0);
	const a = new Cartesian3();
	const b = new Cartesian3();
	const cross = new Cartesian3();

	for (let i = 1; i < positions.length - 1; i++) {
		Cartesian3.subtract(positions[i], origin, a);
		Cartesian3.subtract(positions[i + 1], origin, b);
		Cartesian3.cross(a, b, cross);
		Cartesian3.add(sum, cross, sum);
	}

	if (Cartesian3.magnitude(sum) < 1e-9) return undefined;
	return Cartesian3.normalize(sum, sum);
}

/** Expresses a world (ECEF) direction as east/north/up components at `origin`. */
export function toEastNorthUp(origin: Cartesian3, vector: Cartesian3): Cartesian3 {
	const frame = Transforms.eastNorthUpToFixedFrame(origin);
	const rotation = Matrix4.getMatrix3(frame, new Matrix3());
	// The frame is orthonormal, so its transpose maps world directions back into the local frame.
	Matrix3.transpose(rotation, rotation);
	return Matrix3.multiplyByVector(rotation, vector, new Cartesian3());
}

/**
 * Returns `normal` pointing to the same side as the first hint that is not (nearly)
 * perpendicular to it. Hints are directions towards "outside" the model.
 */
export function chooseSide(normal: Cartesian3, hints: Cartesian3[]): Cartesian3 {
	const normalLength = Cartesian3.magnitude(normal);

	for (const hint of hints) {
		const dot = Cartesian3.dot(normal, hint);
		const decisive = MIN_DECISIVE_COSINE * normalLength * Cartesian3.magnitude(hint);
		if (Math.abs(dot) <= decisive) continue;
		return dot < 0 ? Cartesian3.negate(normal, new Cartesian3()) : normal;
	}

	return normal;
}

/** Heading (clockwise from north) of a camera looking along `direction`; undefined when vertical. */
export function headingAlong(direction: Cartesian3): number | undefined {
	if (Math.hypot(direction.x, direction.y) < MIN_HORIZONTAL) return undefined;
	return CesiumMath.zeroToTwoPi(Math.atan2(direction.x, direction.y));
}

/**
 * Camera heading and pitch that look at a surface with outward normal `normal`.
 * `fallbackHeading` is used when the surface is horizontal.
 */
export function viewFacingNormal(normal: Cartesian3, fallbackHeading: number): { heading: number; pitch: number } {
	const view = Cartesian3.negate(normal, new Cartesian3());
	Cartesian3.normalize(view, view);

	const nearlyVertical = Math.hypot(view.x, view.y) < Math.sin(NEAR_VERTICAL);
	const heading = (nearlyVertical ? undefined : headingAlong(view)) ?? fallbackHeading;
	const alongNormal = Math.asin(CesiumMath.clamp(view.z, -1, 1));
	const pitch = CesiumMath.clamp(alongNormal - TILT_BELOW_NORMAL, -MAX_PITCH, MAX_PITCH);

	return { heading, pitch };
}

/**
 * Where `viewer.zoomTo(target, offset)` puts the camera: `offset` applied in the east/north/up
 * frame at `target`. This is Cesium's `offsetFromHeadingPitchRange`, which it does not export.
 */
export function cameraPositionFor(target: Cartesian3, offset: HeadingPitchRange): Cartesian3 {
	const pitch = CesiumMath.clamp(offset.pitch, -CesiumMath.PI_OVER_TWO, CesiumMath.PI_OVER_TWO);
	const heading = CesiumMath.zeroToTwoPi(offset.heading) - CesiumMath.PI_OVER_TWO;
	const pitchQuat = Quaternion.fromAxisAngle(Cartesian3.UNIT_Y, -pitch, new Quaternion());
	const headingQuat = Quaternion.fromAxisAngle(Cartesian3.UNIT_Z, -heading, new Quaternion());
	const rotation = Matrix3.fromQuaternion(Quaternion.multiply(headingQuat, pitchQuat, headingQuat), new Matrix3());
	const local = Matrix3.multiplyByVector(rotation, Cartesian3.UNIT_X, new Cartesian3());
	Cartesian3.multiplyByScalar(local, -offset.range, local);
	return Matrix4.multiplyByPoint(Transforms.eastNorthUpToFixedFrame(target), local, new Cartesian3());
}

/**
 * Whether the first surface on the line from `from` to `target` is the target itself, i.e.
 * the damage is in plain sight from there. Ray picking ignores back faces, so a line that
 * starts behind a wall passes through it and lands elsewhere, or nowhere. The tiles along
 * the line are loaded first (Cesium's "most detailed" pick), so the answer does not depend
 * on what the camera happens to have in view.
 *
 * Undefined when the scene cannot tell: not in 3D mode, no ray picking, nothing along the
 * line, or tiles that did not arrive within `SIGHT_TIME_LIMIT_MS`.
 */
export async function hasLineOfSight(
	scene: RayPickingScene,
	from: Cartesian3,
	target: Cartesian3,
	exclude: object[],
): Promise<boolean | undefined> {
	if (scene.mode !== SceneMode.SCENE3D) return undefined;

	const direction = Cartesian3.subtract(target, from, new Cartesian3());
	if (Cartesian3.magnitude(direction) < CesiumMath.EPSILON6) return undefined;
	Cartesian3.normalize(direction, direction);
	const ray = new Ray(from, direction);

	let hit: RayHit;
	try {
		if (typeof scene.pickFromRayMostDetailed === 'function') {
			hit = await withTimeLimit(scene.pickFromRayMostDetailed(ray, exclude), SIGHT_TIME_LIMIT_MS);
		} else if (typeof scene.pickFromRay === 'function') {
			hit = scene.pickFromRay(ray, exclude);
		} else {
			return undefined;
		}
	} catch {
		return undefined;
	}
	if (!hit?.position) return undefined;
	return Cartesian3.distance(hit.position, target) <= SIGHT_TOLERANCE;
}

/** The value of `promise`, or undefined once `ms` have passed without it, or if it rejects. */
function withTimeLimit<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
	return new Promise((resolve) => {
		const timer = setTimeout(() => resolve(undefined), ms);
		promise.then(
			(value) => {
				clearTimeout(timer);
				resolve(value);
			},
			() => {
				clearTimeout(timer);
				resolve(undefined);
			},
		);
	});
}

/** Centre of the model nearest to `position`, ignoring world-sized tilesets. */
export function nearestModelCenter(spheres: BoundingSphere[], position: Cartesian3): Cartesian3 | undefined {
	let best: BoundingSphere | undefined;
	let bestDistance = Number.POSITIVE_INFINITY;

	for (const sphere of spheres) {
		if (sphere.radius > MAX_MODEL_RADIUS) continue;
		const distance = Cartesian3.distance(sphere.center, position);
		if (distance < bestDistance) {
			best = sphere;
			bestDistance = distance;
		}
	}

	return best?.center;
}

/** Bounding spheres of every visible tileset in the scene, including nested collections. */
export function collectModelSpheres(primitives: PrimitiveLike, out: BoundingSphere[] = []): BoundingSphere[] {
	for (let i = 0; i < primitives.length; i++) {
		const primitive = primitives.get(i) as ScenePrimitive | undefined;
		if (!primitive || primitive.show === false) continue;

		if (primitive.isLayer === true || primitive instanceof Cesium3DTileset) {
			const sphere = tilesetSphere(primitive);
			if (sphere) out.push(sphere);
		} else if (
			primitive instanceof PrimitiveCollection ||
			(typeof primitive.get === 'function' && typeof primitive.length === 'number')
		) {
			collectModelSpheres(primitive as PrimitiveLike, out);
		}
	}

	return out;
}

/** A tileset that has not finished loading throws on `boundingSphere`; treat it as absent. */
function tilesetSphere(primitive: ScenePrimitive): BoundingSphere | undefined {
	try {
		return primitive.boundingSphere;
	} catch {
		return undefined;
	}
}

function polygonPositions(entity: Entity, time: JulianDate): Cartesian3[] {
	const hierarchy = entity.polygon?.hierarchy?.getValue(time);
	if (!hierarchy) return [];
	return Array.isArray(hierarchy) ? hierarchy : (hierarchy.positions ?? []);
}

/**
 * Offset for `viewer.flyTo` / `viewer.zoomTo` that looks at a damage from in front of the
 * surface it sits on. "In front" is the side from which the geometry has the damage in plain
 * sight; the tiles around the damage are loaded to find out, which is why this is async.
 * When the scene cannot tell, it is the side the camera is currently on, and failing that
 * the side away from the nearest model's centre.
 */
export async function getDamageViewOffset(viewer: Viewer, entity: Entity): Promise<HeadingPitchRange> {
	const time = viewer.clock.currentTime;
	const positions = polygonPositions(entity, time);
	const isPolygon = defined(entity.polygon);
	const range = isPolygon ? POLYGON_RANGE : POINT_RANGE;
	const fallbackPitch = isPolygon ? POLYGON_FALLBACK_PITCH : POINT_PITCH;

	const center = positions.length > 0 ? BoundingSphere.fromPoints(positions).center : entity.position?.getValue(time);
	if (!defined(center)) return new HeadingPitchRange(viewer.camera.heading, fallbackPitch, range);

	// Directions towards "outside", most trustworthy first. The camera comes first: a damage
	// is drawn, and clicked, while it is in view. A model's bounding-sphere centre is a weak
	// hint: on a bridge it sits in the deck, which is *outside* a pier face below it.
	const hints: Cartesian3[] = [Cartesian3.subtract(viewer.camera.positionWC, center, new Cartesian3())];
	const modelCenter = nearestModelCenter(collectModelSpheres(viewer.scene.primitives), center);
	if (modelCenter) hints.push(Cartesian3.subtract(center, modelCenter, new Cartesian3()));
	const outward = hints.map((hint) => toEastNorthUp(center, hint));

	// Without a surface normal, look at the damage from outside inwards.
	const fallbackHeading =
		outward.map((hint) => headingAlong(Cartesian3.negate(hint, new Cartesian3()))).find(defined) ??
		viewer.camera.heading;

	const normal = polygonNormal(positions);
	if (!normal) return new HeadingPitchRange(fallbackHeading, fallbackPitch, range);

	// Both sides of the surface are candidates; the geometry says from which one the damage is
	// the first thing in view. The polygon itself sits on the surface, so it is left out.
	const normalEnu = toEastNorthUp(center, normal);
	const sides = [normalEnu, Cartesian3.negate(normalEnu, new Cartesian3())];
	const views = sides.map((side) => viewFacingNormal(side, fallbackHeading));
	const inSight = await Promise.all(
		views.map((view) => {
			const spot = cameraPositionFor(center, new HeadingPitchRange(view.heading, view.pitch, range));
			return hasLineOfSight(viewer.scene, spot, center, [entity]);
		}),
	);
	const seenFrom = inSight.findIndex((seen, i) => seen === true && inSight[1 - i] !== true);
	if (seenFrom >= 0) return new HeadingPitchRange(views[seenFrom].heading, views[seenFrom].pitch, range);

	const view = viewFacingNormal(chooseSide(normalEnu, outward), fallbackHeading);
	return new HeadingPitchRange(view.heading, view.pitch, range);
}
